import React, { useEffect, useState } from 'react';
import { Plus, Quote, Star, Eye, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { quoteService } from '../services/quoteService';
import { Quote as QuoteModel } from '@brovicms/models';
import QuoteTable from '../components/quotes/QuoteTable';
import AddQuoteModal from '../components/quotes/AddQuoteModal';

const Quotes = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState<QuoteModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    featured: 0,
    drafts: 0
  });

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const result = await quoteService.getQuotes();
      setQuotes(result.data || []);
      updateStats(result.data || []);
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateStats = (quotesData: QuoteModel[]) => {
    setStats({
      total: quotesData.length,
      published: quotesData.filter(q => q.isPublished).length,
      featured: quotesData.filter(q => q.isFeatured).length,
      drafts: quotesData.filter(q => !q.isPublished).length
    });
  };


  const handleRefresh = () => {
    fetchQuotes();
  };

  const handleAddSuccess = () => {
    fetchQuotes();
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-heading text-primary-800">Quotes</h1>
          <p className="text-gray-600 mt-1">
            Manage your inspirational quotes collection
          </p>
        </div>
        <button 
          className="btn btn-primary" 
          onClick={() => setShowAddModal(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Quote
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-primary-100 rounded-apple">
                <Quote className="w-5 h-5 text-primary-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Quotes</p>
                <p className="text-lg font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-success-100 rounded-apple">
                <Eye className="w-5 h-5 text-success-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Published</p>
                <p className="text-lg font-semibold text-gray-900">{stats.published}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-warning-100 rounded-apple">
                <Star className="w-5 h-5 text-warning-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Featured</p>
                <p className="text-lg font-semibold text-gray-900">{stats.featured}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-apple">
                <BookOpen className="w-5 h-5 text-gray-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Drafts</p>
                <p className="text-lg font-semibold text-gray-900">{stats.drafts}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quotes Table */}
            <QuoteTable
              quotes={quotes}
              onRefresh={handleRefresh}
              loading={loading}
            />

      {/* Add Quote Modal */}
      <AddQuoteModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={handleAddSuccess}
      />
    </div>
  );
};

export default Quotes;
